import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { VENDOR_CURRENCIES, convertToUsd, rateForCurrency } from '@classes/vendor-cost';
import { Vendor } from '@models/vendor';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { SettingsService } from '@services/settings.service';
import { VendorInvoiceScanService } from '@services/vendor-invoice-scan.service';
import { VendorOrderService } from '@services/vendor-order.service';
import { VendorService } from '@services/vendor.service';
import ObjectID from 'bson-objectid';
import { Subject, take, takeUntil } from 'rxjs';

type EntryType = 'invoice' | 'credit';

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
};

function requiredInitials(control: AbstractControl) {
  return String(control.value || '').trim() ? null : { required: true };
}

function money(value: unknown): number {
  const parsed = parseFloat(String(value ?? '').replace(/,/g, ''));
  return isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
}

@Component({
  selector: 'app-invoices-credits',
  templateUrl: './invoices-credits.component.html',
  styleUrls: ['./invoices-credits.component.css'],
})
export class InvoicesCreditsComponent implements OnInit, OnDestroy {
  currencies = [...VENDOR_CURRENCIES];
  vendors: Vendor[] = [];
  form: FormGroup;
  saving = false;
  scanBusy = false;
  duplicateInvoice = false;
  attachment: {
    fileName?: string;
    originalName?: string;
    contentType?: string;
    uploadedAt?: string;
    uploadError?: boolean;
  } | null = null;

  private unsubscribe$ = new Subject<void>();
  private logId = new ObjectID().toString();
  private orderId = new ObjectID().toString();

  constructor(
    private vendorOrderService: VendorOrderService,
    private vendorService: VendorService,
    private vendorInvoiceScanService: VendorInvoiceScanService,
    private settingsService: SettingsService,
    private notificationService: NotificationService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    this.form = this.buildForm();
    this.form
      .get('vendor')
      .valueChanges.pipe(takeUntil(this.unsubscribe$))
      .subscribe((vendorId) => this.applyVendorCurrency(vendorId));
    this.vendorService
      .getVendorList()
      .pipe(take(1))
      .subscribe((vendors: Vendor[]) => {
        this.vendors = (vendors || []).filter((vendor) => !vendor.distributor);
      });
    this.settingsService.loadSettings().pipe(take(1)).subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get entryType(): EntryType {
    return this.form?.value?.entryType === 'credit' ? 'credit' : 'invoice';
  }

  get kindLabel(): string {
    return this.entryType === 'credit' ? 'Credit' : 'Invoice';
  }

  get uploadLabel(): string {
    if (this.scanBusy) {
      return 'Reading…';
    }
    return this.entryType === 'credit'
      ? 'Scan or upload credit memo'
      : 'Scan or upload invoice';
  }

  get currency(): string {
    return this.form?.value?.currency || 'USD';
  }

  get currencySymbol(): string {
    return CURRENCY_SYMBOLS[this.currency] || this.currency;
  }

  get total(): number {
    if (!this.form) {
      return 0;
    }
    const { shipping, tax, price } = this.form.value;
    return money(money(shipping) + money(tax) + money(price));
  }

  get exchangeRate(): number {
    return rateForCurrency(this.currency, this.settingsService.exchangeRates);
  }

  get usdTotal(): number {
    const signed = this.entryType === 'credit' ? -Math.abs(this.total) : this.total;
    return convertToUsd(signed, this.exchangeRate);
  }

  get vendorWidth(): string {
    const vendor = this.vendors.find((item) => item._id === this.form?.value?.vendor);
    const length = (vendor?.name || 'Vendor').length;
    return `${Math.min(42, Math.max(16, length + 4))}ch`;
  }

  moneyLabel(name: string): string {
    return `${name} (${this.currency})`;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    if (input) {
      input.value = '';
    }
    if (!file) {
      return;
    }
    const allowed =
      file.type === 'application/pdf' ||
      file.type.startsWith('image/') ||
      /\.(pdf|png|jpe?g|gif|webp|heic|tif{1,2})$/i.test(file.name);
    if (!allowed) {
      this.notificationService.show('error', 'Upload a PDF, photo, or scan.');
      return;
    }
    if (!this.form.value.vendor) {
      this.notificationService.show('error', 'Select a vendor before uploading.');
      return;
    }

    this.scanBusy = true;
    this.duplicateInvoice = false;
    this.vendorInvoiceScanService
      .scanInvoice(file, String(this.form.value.vendor), [])
      .subscribe({
        next: (proposal) => {
          this.scanBusy = false;
          this.attachment = proposal?.invoiceAttachment || {
            originalName: file.name,
            contentType: file.type,
            uploadedAt: new Date().toISOString(),
          };
          this.duplicateInvoice = !!proposal?.duplicateInvoice;
          this.applyProposal(proposal?.header);
        },
        error: () => {
          this.scanBusy = false;
          this.notificationService.show('error', 'Unable to read the uploaded file.');
        },
      });
  }

  cancel(): void {
    this.resetForm();
  }

  save(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    const sign = this.entryType === 'credit' ? -1 : 1;
    const shipping = money(Math.abs(money(value.shipping)) * sign);
    const tax = money(Math.abs(money(value.tax)) * sign);
    const price = money(Math.abs(money(value.price)) * sign);
    const total = money(shipping + tax + price);
    const rate = this.exchangeRate;
    const usdTotal = convertToUsd(total, rate);
    const initials = String(value.initials || '').trim().toUpperCase();
    const documentNumber = String(value.invoiceNumber || '').trim();
    const employeeNotes = String(value.notes || '').trim();
    const conversionNote = this.conversionNote(
      documentNumber,
      value.currency,
      shipping,
      tax,
      price,
      total,
      rate,
      usdTotal
    );

    const payload: any = {
      _id: this.orderId,
      vendor: value.vendor,
      type: 'order',
      borrowingStore: null,
      groupId: '',
      raNumber: '',
      trackingNumber: '',
      proposedCredit: '',
      datePaid: null,
      archived: false,
      products: [],
      orders: [],
      status: this.entryType === 'credit' ? 'CREDITED' : 'RECEIVED',
      invoiceNumber: documentNumber,
      date: value.date,
      dateAdded: value.date,
      shipping,
      tax,
      price,
      total,
      amountDue: total,
      currency: value.currency,
      exchangeRate: rate,
      usdTotal,
      invoiceAttachment: this.attachment
        ? {
            fileName: this.attachment.fileName || null,
            originalName: this.attachment.originalName || null,
            contentType: this.attachment.contentType || null,
            uploadedAt: this.attachment.uploadedAt || new Date().toISOString(),
          }
        : undefined,
      log: [
        {
          _id: this.logId,
          item: this.entryType === 'credit' ? 'CREDIT' : 'INVOICE',
          notes: employeeNotes ? `${employeeNotes}\n${conversionNote}` : conversionNote,
          initials,
          date: new Date().toISOString(),
          user: this.auth.currentUser?._id,
        },
      ],
    };

    this.saving = true;
    this.vendorOrderService.addVendorOrder(payload).subscribe({
      next: () => {
        this.saving = false;
        this.notificationService.show(
          'success',
          this.entryType === 'credit' ? 'CREDIT SAVED' : 'INVOICE SAVED'
        );
        this.resetForm();
      },
      error: (err) => {
        this.saving = false;
        this.notificationService.show(
          'error',
          err?.error?.message || 'Unable to save.'
        );
      },
    });
  }

  private applyVendorCurrency(vendorId: string): void {
    const vendor = this.vendors.find((item) => item._id === vendorId);
    const currency = vendor?.currency;
    if (currency && this.currencies.includes(currency as typeof VENDOR_CURRENCIES[number])) {
      this.form.patchValue({ currency });
    }
  }

  private applyProposal(header: {
    invoiceNumber?: string;
    invoiceDate?: string;
    shipping?: number;
    tax?: number;
    subtotal?: number;
    total?: number;
  } | undefined): void {
    if (!header) {
      return;
    }
    const patch: Record<string, unknown> = {};
    if (header.invoiceNumber) {
      patch.invoiceNumber = header.invoiceNumber;
    }
    if (header.invoiceDate) {
      const parsed = new Date(header.invoiceDate);
      if (!isNaN(parsed.getTime())) {
        patch.date = parsed;
      }
    }
    if (header.shipping != null && !isNaN(Number(header.shipping))) {
      patch.shipping = Math.abs(Number(header.shipping));
    }
    if (header.tax != null && !isNaN(Number(header.tax))) {
      patch.tax = Math.abs(Number(header.tax));
    }
    const shipping = header.shipping != null ? Math.abs(Number(header.shipping)) : money(this.form.value.shipping);
    const tax = header.tax != null ? Math.abs(Number(header.tax)) : money(this.form.value.tax);
    if (header.subtotal != null && !isNaN(Number(header.subtotal))) {
      patch.price = Math.abs(Number(header.subtotal));
    } else if (header.total != null && !isNaN(Number(header.total))) {
      patch.price = money(Math.abs(Number(header.total)) - shipping - tax);
    }
    this.form.patchValue(patch);
    this.form.markAsDirty();
  }

  private conversionNote(
    documentNumber: string,
    currency: string,
    shipping: number,
    tax: number,
    price: number,
    total: number,
    rate: number,
    usdTotal: number
  ): string {
    return [
      `${this.kindLabel} ${documentNumber}.`,
      `${currency} shipping ${shipping.toFixed(2)}, tax ${tax.toFixed(2)}, price ${price.toFixed(2)}, total ${total.toFixed(2)}.`,
      `Rate ${rate}. USD ${usdTotal.toFixed(2)}.`,
    ].join(' ');
  }

  private buildForm(): FormGroup {
    return new FormGroup({
      entryType: new FormControl('invoice'),
      vendor: new FormControl(null, Validators.required),
      currency: new FormControl('USD', Validators.required),
      invoiceNumber: new FormControl('', Validators.required),
      date: new FormControl(null, Validators.required),
      shipping: new FormControl(null),
      tax: new FormControl(null),
      price: new FormControl(null),
      notes: new FormControl(''),
      initials: new FormControl('', requiredInitials),
    });
  }

  private resetForm(): void {
    this.orderId = new ObjectID().toString();
    this.logId = new ObjectID().toString();
    this.attachment = null;
    this.duplicateInvoice = false;
    this.form.reset({
      entryType: 'invoice',
      vendor: null,
      currency: 'USD',
      invoiceNumber: '',
      date: null,
      shipping: null,
      tax: null,
      price: null,
      notes: '',
      initials: '',
    });
  }
}
